import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Upload,
  MoreVertical,
  Loader2,
  ArrowLeft,
  RefreshCw,
} from "lucide-react";
import {
  ExportCurve,
  Play,
  Pause,
  DocumentDownload,
  VolumeHigh,
  VolumeCross,
  Forward,
  Maximize2,
  Profile,
} from "iconsax-reactjs";

import AskGenziButton from "./AskGenziButton";
import api from "../../services/api";

const MediaPlayback2 = () => {
  const { meetingId } = useParams();
  const navigate = useNavigate();

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const [meeting, setMeeting] = useState(null);

  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const [retrying, setRetrying] = useState(false);
  useEffect(() => {
    fetchMeetingDetails();
  }, [meetingId]);

  const fetchMeetingDetails = async () => {
    try {
      setLoading(true);
      const response = await api.getMeetingDetails(meetingId);

      if (response.success) {
        setMeeting(response.meeting);
        setAudioDuration(response.meeting.duration || 0);
      }
    } catch (error) {
      // console.error("Failed to fetch meeting details:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleRetryProcessing = async () => {
    if (
      !window.confirm(
        "This will delete the current transcript, insights, and action items, and reprocess the meeting. Continue?"
      )
    ) {
      return;
    }

    try {
      setRetrying(true);
      const response = await api.retryMeetingProcessing(meetingId);

      if (response.success) {
        // Show success message
        alert("Meeting reprocessing started! This may take a few minutes.");

        // Refresh the meeting data after a delay
        setTimeout(() => {
          fetchMeetingDetails();
        }, 5000);
      }
    } catch (error) {
      console.error("Failed to retry meeting processing:", error);
      // alert("Failed to start reprocessing. Please try again.");
    } finally {
      setRetrying(false);
    }
  };
  const formatTime = (seconds) => {
    const secs = Math.max(0, Math.floor(seconds));
    const mins = Math.floor(secs / 60);
    const rem = secs % 60;
    return `${mins}:${rem.toString().padStart(2, "0")}`;
  };

  const handleAudioLoadedMetadata = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration || 0);
      setCurrentTime(0);
    }
  };

  const handleVideoLoadedMetadata = () => {
    if (videoRef.current) {
      setAudioDuration(videoRef.current.duration || 0);
      setCurrentTime(0);
    }
  };

  const handleTimeUpdate = () => {
    const media = videoRef.current || audioRef.current;
    if (media) {
      setCurrentTime(Math.floor(media.currentTime));
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
  };

  const handleSeek = (e) => {
    const media = videoRef.current || audioRef.current;
    if (media) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const percentage = Math.min(1, Math.max(0, clickX / width));
      const newTime = percentage * audioDuration;

      media.currentTime = newTime;
      setCurrentTime(Math.floor(newTime));
    }
  };

  const togglePlayPause = () => {
    const media = videoRef.current || audioRef.current;
    if (media) {
      if (isPlaying) {
        media.pause();
      } else {
        media.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    const media = videoRef.current || audioRef.current;
    if (media) {
      media.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const progressPercentage =
    audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0;

  const getSpeakers = () => {
    if (!meeting?.transcripts) return [];

    const speakerSet = new Set();
    meeting.transcripts.forEach((transcript) => {
      if (transcript.speakerName) {
        speakerSet.add(transcript.speakerName);
      }
    });

    return Array.from(speakerSet).slice(0, 4);
  };

  const getActionItems = () => {
    if (!meeting?.insights?.actionItems) return [];

    if (Array.isArray(meeting.insights.actionItems)) {
      return meeting.insights.actionItems;
    }

    return [];
  };

  const getTopics = () => {
    if (meeting?.insights?.tags && Array.isArray(meeting.insights.tags)) {
      return meeting.insights.tags;
    }

    if (
      meeting?.insights?.keyTopics &&
      Array.isArray(meeting.insights.keyTopics)
    ) {
      return meeting.insights.keyTopics.map((topic) =>
        typeof topic === "string" ? topic : topic.name || topic.topic
      );
    }

    return ["Meeting"];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0C0E12] text-white flex items-center justify-center">
        <Loader2 size={48} className="animate-spin text-purple-500" />
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="min-h-screen bg-[#0C0E12] text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">Meeting not found</p>
          <button
            onClick={() => navigate("/media-playback")}
            className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
          >
            Back to Meetings
          </button>
        </div>
      </div>
    );
  }

  const speakers = getSpeakers();
  const actionItems = getActionItems();
  const topics = getTopics();
  const hasVideo = !!meeting.processedRecordingUrl || !!meeting.recordingUrl;

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white">
      <div className="p-4 md:p-8 mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="flex-1 min-w-0">
            <button
              onClick={() => navigate("/media-playback")}
              className="flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Back to Meetings</span>
            </button>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 text-white">
              {meeting.title || "Meeting Recording"}
            </h1>
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
              {new Date(
                meeting.scheduledAt || meeting.createdAt
              ).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-2 sm:gap-3 shrink-0">
            <button
              onClick={handleRetryProcessing}
              disabled={retrying || !meeting?.recordingUrl}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm sm:text-base font-medium min-w-0 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {retrying ? (
                <Loader2 size={16} className="shrink-0 animate-spin" />
              ) : (
                <RefreshCw size={16} className="shrink-0" />
              )}
              <span className="whitespace-nowrap">
                {retrying ? "Reprocessing..." : "Reprocess"}
              </span>
            </button>
            <button className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm sm:text-base font-medium min-w-0">
              <ExportCurve size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Export</span>
            </button>

            <button
              onClick={() => api.downloadTranscript(meetingId, "txt")}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
                border: "1px solid",
                borderImageSource:
                  "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                boxShadow: `
                  0px 36px 32px -16px #7F56D936,
                  0px 8px 16px 0px #7F56D91F,
                  0px 2px 4px -8px #7F56D914,
                  0px 8px 24px -16px #FFFFFF3D inset,
                  0px -24px 32px 0px #FFFFFF38 inset,
                  0px 0px 12px 0px #FFFFFF inset
                `,
                borderImageSlice: "1",
                transition: "all 0.3s ease",
              }}
            >
              <DocumentDownload size={16} className="shrink-0 text-white" />
              <span className="whitespace-nowrap">Download</span>
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          <div className="lg:w-1/2 space-y-6">
            <div className="">
              {hasVideo && meeting.processedRecordingUrl ? (
                <video
                  ref={videoRef}
                  src={meeting.processedRecordingUrl}
                  className="w-full max-h-[40vh] sm:max-h-[45vh] md:max-h-[50vh] lg:max-h-[55vh] object-contain  rounded-lg mb-4"
                  onLoadedMetadata={handleVideoLoadedMetadata}
                  onTimeUpdate={handleTimeUpdate}
                  onEnded={handleEnded}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                />
              ) : null}

              <div className="flex items-center gap-4 w-full">
                <button
                  onClick={togglePlayPause}
                  disabled={!hasVideo}
                  className="w-12 h-12 rounded-full flex items-center justify-center transition-colors flex-shrink-0 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPlaying ? (
                    <Pause size={20} className="text-[#61656C]" />
                  ) : (
                    <Play size={20} className="text-[#61656C]" />
                  )}
                </button>

                <div className="flex-1 md:flex items-center gap-4 max-w-2xl mx-4 hidden">
                  <span className="text-sm text-gray-400 min-w-[40px] text-right">
                    {formatTime(currentTime)}
                  </span>
                  <div
                    className="flex-1 relative cursor-pointer"
                    onClick={handleSeek}
                  >
                    <div className="h-2 bg-gray-700 rounded-full">
                      <div
                        className="h-2 bg-[#7F56D9] rounded-full relative"
                        style={{ width: `${progressPercentage}%` }}
                      >
                        <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  <span className="text-sm text-gray-400 min-w-[40px]">
                    {formatTime(Math.floor(audioDuration))}
                  </span>
                </div>

                <div className="flex items-center flex-shrink-0">
                  <button
                    onClick={toggleMute}
                    className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    {isMuted ? (
                      <VolumeCross size={15} className="text-[#61656C]" />
                    ) : (
                      <VolumeHigh size={15} className="text-[#61656C]" />
                    )}
                  </button>
                  <button className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
                    <Forward size={15} className="text-[#61656C]" />
                  </button>
                  <button className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
                    <Maximize2 size={15} className="text-[#61656C]" />
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6 space-y-6">
              <div>
                <h2 className="text-lg font-semibold mb-4">Summary</h2>
                <p className="text-sm text-gray-300 leading-relaxed">
                  {meeting.insights?.summary || "No summary available"}
                </p>
              </div>

              <div>
                <h2 className="text-lg font-semibold mb-4">Topics</h2>
                <div className="flex flex-wrap gap-2">
                  {topics.map((topic, index) => (
                    <span
                      key={index}
                      className={`px-3 py-1 text-xs rounded-full ${
                        index === 0 ? "bg-blue-600" : "bg-gray-700"
                      }`}
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="text-lg font-semibold mb-4">Action Items</h2>
                <div className="space-y-4">
                  {actionItems.length > 0 ? (
                    actionItems.map((speakerActions, index) => (
                      <div key={index}>
                        <h3 className="text-sm font-medium text-white mb-2">
                          {speakerActions.speaker || `Person ${index + 1}`}
                        </h3>
                        <div className="space-y-3">
                          {Array.isArray(speakerActions.items) ? (
                            speakerActions.items.map((item, itemIndex) => (
                              <p
                                key={itemIndex}
                                className="text-xs text-[#94979C] leading-relaxed"
                              >
                                {item}
                              </p>
                            ))
                          ) : (
                            <p className="text-xs text-[#94979C] leading-relaxed">
                              No action items
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-[#94979C] leading-relaxed">
                      No action items available
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="lg:w-1/2">
            <div className="bg-[#13161B] border border-[#22262F] rounded-xl h-full">
              <div className="flex items-center justify-between p-6 border-b border-[#22262F]">
                <h2 className="text-lg font-semibold">Transcript</h2>
                <button className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
                  <MoreVertical size={20} className="text-gray-300" />
                </button>
              </div>

              <div className="p-6 space-y-6 h-[calc(100%-64px)] overflow-y-auto">
                {meeting.transcripts && meeting.transcripts.length > 0 ? (
                  meeting.transcripts.map((transcript, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="flex flex-col items-center min-w-[60px]">
                        <span className="text-xs text-gray-400">
                          {formatTime(transcript.startTime)}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-white mb-1">
                          {transcript.speakerName || "Unknown Speaker"}:{" "}
                          <p className="text-sm text-[#94979C] leading-relaxed">
                            {transcript.text}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <p className="text-gray-400">No transcript available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <AskGenziButton />
    </div>
  );
};

export default MediaPlayback2;
